import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { SEO } from '../components/SEO';

const privacyContent = {
  it: {
    title: 'Informativa sulla Privacy',
    lastUpdate: 'Ultimo aggiornamento: 23 Febbraio 2026',
    sections: [
      {
        title: '1. Titolare del Trattamento',
        content: `Il Titolare del trattamento dei dati è lo Studio Notarile Associato Accordini & Carillo, con sede legale in Via Don Giovanni Beltrame 22, 37067 Valeggio Sul Mincio (VR), P.IVA IT05171120230.

Per qualsiasi informazione relativa al trattamento dei dati personali potete contattarci:
- Email: privacy@accordinicarillo.it
- Telefono: +39 045 6370799
- PEC: accordinicarillo@pec.it`
      },
      {
        title: '2. Tipologie di Dati Raccolti',
        content: `I dati personali raccolti attraverso questo sito includono:

**Dati di navigazione:** indirizzo IP, tipo di browser, sistema operativo, pagine visitate, data e ora di accesso.

**Dati forniti volontariamente:** nome, cognome, email, telefono e altri dati inseriti nei moduli di contatto.

**Cookie:** informazioni raccolte tramite cookie tecnici e, previo consenso, cookie analitici. Per maggiori dettagli consultare la Cookie Policy.`
      },
      {
        title: '3. Finalità del Trattamento',
        content: `I dati personali sono trattati per le seguenti finalità:

a) **Risposta alle richieste di contatto** - Base giuridica: esecuzione di misure precontrattuali (art. 6.1.b GDPR)

b) **Erogazione dei servizi notarili richiesti** - Base giuridica: esecuzione del contratto (art. 6.1.b GDPR)

c) **Adempimenti di legge** - Base giuridica: obbligo legale (art. 6.1.c GDPR)

d) **Analisi statistiche anonime sul traffico del sito** - Base giuridica: consenso (art. 6.1.a GDPR)`
      },
      {
        title: '4. Modalità di Trattamento',
        content: `I dati personali sono trattati con strumenti automatizzati per il tempo strettamente necessario a conseguire gli scopi per cui sono stati raccolti.

Specifiche misure di sicurezza sono osservate per prevenire la perdita dei dati, usi illeciti o non corretti ed accessi non autorizzati.

I dati sono conservati su server sicuri situati all'interno dell'Unione Europea.`
      },
      {
        title: '5. Conservazione dei Dati',
        content: `I dati personali saranno conservati per i seguenti periodi:

- **Dati di contatto:** fino all'evasione della richiesta e per i successivi 24 mesi
- **Dati relativi agli atti notarili:** secondo gli obblighi di legge (conservazione illimitata per gli atti pubblici)
- **Dati di navigazione:** 12 mesi
- **Cookie analitici:** secondo quanto indicato nella Cookie Policy`
      },
      {
        title: '6. Comunicazione e Diffusione dei Dati',
        content: `I dati personali potranno essere comunicati a:

- Autorità pubbliche per adempimenti di legge
- Fornitori di servizi tecnici e informatici (responsabili del trattamento)
- Collaboratori dello studio per l'erogazione dei servizi

I dati non saranno diffusi né trasferiti al di fuori dell'Unione Europea.`
      },
      {
        title: '7. Diritti dell\'Interessato',
        content: `In qualità di interessato, avete il diritto di:

- **Accesso** (art. 15 GDPR): ottenere conferma dell'esistenza dei vostri dati
- **Rettifica** (art. 16 GDPR): correggere dati inesatti
- **Cancellazione** (art. 17 GDPR): richiedere la cancellazione dei dati
- **Limitazione** (art. 18 GDPR): limitare il trattamento
- **Portabilità** (art. 20 GDPR): ricevere i dati in formato strutturato
- **Opposizione** (art. 21 GDPR): opporvi al trattamento
- **Revoca del consenso** (art. 7 GDPR): revocare il consenso prestato

Per esercitare i vostri diritti potete contattarci all'indirizzo privacy@accordinicarillo.it

Avete inoltre il diritto di proporre reclamo all'Autorità Garante per la Protezione dei Dati Personali (www.garanteprivacy.it).`
      },
      {
        title: '8. Modifiche alla Privacy Policy',
        content: `La presente informativa può essere soggetta a modifiche. Vi invitiamo a consultare periodicamente questa pagina per essere aggiornati sulle modalità di trattamento dei vostri dati.`
      }
    ]
  },
  en: {
    title: 'Privacy Policy',
    lastUpdate: 'Last updated: February 23, 2026',
    sections: [
      {
        title: '1. Data Controller',
        content: `The Data Controller is Studio Notarile Associato Accordini & Carillo, with registered office at Via Don Giovanni Beltrame 22, 37067 Valeggio Sul Mincio (VR), Italy, VAT IT05171120230.

For any information regarding the processing of personal data, you can contact us:
- Email: privacy@accordinicarillo.it
- Phone: +39 045 6370799
- PEC: accordinicarillo@pec.it`
      },
      {
        title: '2. Types of Data Collected',
        content: `Personal data collected through this website includes:

**Navigation data:** IP address, browser type, operating system, pages visited, date and time of access.

**Data provided voluntarily:** name, surname, email, phone and other data entered in contact forms.

**Cookies:** information collected through technical cookies and, with consent, analytical cookies. For more details, see the Cookie Policy.`
      },
      {
        title: '3. Purposes of Processing',
        content: `Personal data is processed for the following purposes:

a) **Responding to contact requests** - Legal basis: execution of pre-contractual measures (art. 6.1.b GDPR)

b) **Provision of requested notarial services** - Legal basis: contract execution (art. 6.1.b GDPR)

c) **Legal compliance** - Legal basis: legal obligation (art. 6.1.c GDPR)

d) **Anonymous statistical analysis of site traffic** - Legal basis: consent (art. 6.1.a GDPR)`
      },
      {
        title: '4. Processing Methods',
        content: `Personal data is processed with automated tools for the time strictly necessary to achieve the purposes for which it was collected.

Specific security measures are observed to prevent data loss, unlawful or incorrect use, and unauthorized access.

Data is stored on secure servers located within the European Union.`
      },
      {
        title: '5. Data Retention',
        content: `Personal data will be retained for the following periods:

- **Contact data:** until the request is fulfilled and for the following 24 months
- **Data related to notarial deeds:** according to legal obligations (unlimited retention for public deeds)
- **Navigation data:** 12 months
- **Analytical cookies:** as indicated in the Cookie Policy`
      },
      {
        title: '6. Data Communication and Disclosure',
        content: `Personal data may be communicated to:

- Public authorities for legal compliance
- Technical and IT service providers (data processors)
- Firm collaborators for service provision

Data will not be disclosed or transferred outside the European Union.`
      },
      {
        title: '7. Data Subject Rights',
        content: `As a data subject, you have the right to:

- **Access** (art. 15 GDPR): obtain confirmation of the existence of your data
- **Rectification** (art. 16 GDPR): correct inaccurate data
- **Erasure** (art. 17 GDPR): request data deletion
- **Restriction** (art. 18 GDPR): restrict processing
- **Portability** (art. 20 GDPR): receive data in structured format
- **Object** (art. 21 GDPR): object to processing
- **Withdraw consent** (art. 7 GDPR): withdraw given consent

To exercise your rights, contact us at privacy@accordinicarillo.it

You also have the right to lodge a complaint with the Italian Data Protection Authority (www.garanteprivacy.it).`
      },
      {
        title: '8. Changes to Privacy Policy',
        content: `This policy may be subject to changes. We invite you to periodically check this page to stay updated on how your data is processed.`
      }
    ]
  },
  de: {
    title: 'Datenschutzerklärung',
    lastUpdate: 'Letzte Aktualisierung: 23. Februar 2026',
    sections: [
      {
        title: '1. Verantwortlicher',
        content: `Der Verantwortliche für die Datenverarbeitung ist Studio Notarile Associato Accordini & Carillo, mit Sitz in Via Don Giovanni Beltrame 22, 37067 Valeggio Sul Mincio (VR), Italien, USt-IdNr. IT05171120230.

Für Informationen zur Verarbeitung personenbezogener Daten kontaktieren Sie uns:
- E-Mail: privacy@accordinicarillo.it
- Telefon: +39 045 6370799
- PEC: accordinicarillo@pec.it`
      },
      {
        title: '2. Arten der erhobenen Daten',
        content: `Über diese Website erhobene personenbezogene Daten umfassen:

**Navigationsdaten:** IP-Adresse, Browsertyp, Betriebssystem, besuchte Seiten, Datum und Uhrzeit des Zugriffs.

**Freiwillig bereitgestellte Daten:** Name, Nachname, E-Mail, Telefon und andere in Kontaktformularen eingegebene Daten.

**Cookies:** Durch technische Cookies und mit Zustimmung durch analytische Cookies gesammelte Informationen. Weitere Details finden Sie in der Cookie-Richtlinie.`
      },
      {
        title: '3. Verarbeitungszwecke',
        content: `Personenbezogene Daten werden für folgende Zwecke verarbeitet:

a) **Beantwortung von Kontaktanfragen** - Rechtsgrundlage: Durchführung vorvertraglicher Maßnahmen (Art. 6 Abs. 1 lit. b DSGVO)

b) **Erbringung angeforderter Notardienstleistungen** - Rechtsgrundlage: Vertragserfüllung (Art. 6 Abs. 1 lit. b DSGVO)

c) **Gesetzliche Erfüllung** - Rechtsgrundlage: rechtliche Verpflichtung (Art. 6 Abs. 1 lit. c DSGVO)

d) **Anonyme statistische Analyse des Website-Verkehrs** - Rechtsgrundlage: Einwilligung (Art. 6 Abs. 1 lit. a DSGVO)`
      },
      {
        title: '7. Rechte der betroffenen Person',
        content: `Als betroffene Person haben Sie das Recht auf:

- **Auskunft** (Art. 15 DSGVO): Bestätigung über die Existenz Ihrer Daten erhalten
- **Berichtigung** (Art. 16 DSGVO): unrichtige Daten korrigieren
- **Löschung** (Art. 17 DSGVO): Datenlöschung verlangen
- **Einschränkung** (Art. 18 DSGVO): Verarbeitung einschränken
- **Datenübertragbarkeit** (Art. 20 DSGVO): Daten in strukturiertem Format erhalten
- **Widerspruch** (Art. 21 DSGVO): der Verarbeitung widersprechen
- **Widerruf der Einwilligung** (Art. 7 DSGVO): erteilte Einwilligung widerrufen

Zur Ausübung Ihrer Rechte kontaktieren Sie uns unter privacy@accordinicarillo.it`
      }
    ]
  },
  fr: {
    title: 'Politique de Confidentialité',
    lastUpdate: 'Dernière mise à jour: 23 février 2026',
    sections: [
      {
        title: '1. Responsable du Traitement',
        content: `Le Responsable du traitement des données est Studio Notarile Associato Accordini & Carillo, avec siège social Via Don Giovanni Beltrame 22, 37067 Valeggio Sul Mincio (VR), Italie, TVA IT05171120230.

Pour toute information concernant le traitement des données personnelles, contactez-nous:
- Email: privacy@accordinicarillo.it
- Téléphone: +39 045 6370799
- PEC: accordinicarillo@pec.it`
      },
      {
        title: '2. Types de Données Collectées',
        content: `Les données personnelles collectées via ce site incluent:

**Données de navigation:** adresse IP, type de navigateur, système d'exploitation, pages visitées, date et heure d'accès.

**Données fournies volontairement:** nom, prénom, email, téléphone et autres données saisies dans les formulaires de contact.

**Cookies:** informations collectées via des cookies techniques et, avec consentement, des cookies analytiques. Pour plus de détails, consultez la Politique des Cookies.`
      },
      {
        title: '7. Droits de la Personne Concernée',
        content: `En tant que personne concernée, vous avez le droit de:

- **Accès** (art. 15 RGPD): obtenir confirmation de l'existence de vos données
- **Rectification** (art. 16 RGPD): corriger des données inexactes
- **Effacement** (art. 17 RGPD): demander la suppression des données
- **Limitation** (art. 18 RGPD): limiter le traitement
- **Portabilité** (art. 20 RGPD): recevoir les données dans un format structuré
- **Opposition** (art. 21 RGPD): s'opposer au traitement
- **Retrait du consentement** (art. 7 RGPD): retirer le consentement donné

Pour exercer vos droits, contactez-nous à privacy@accordinicarillo.it`
      }
    ]
  }
};

const PrivacyPolicyPage = () => {
  const { language } = useLanguage();
  const content = privacyContent[language] || privacyContent.it;

  return (
    <div data-testid="privacy-policy-page" className="min-h-screen">
      <SEO page="privacy" />
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-[#283880]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-white mb-4">
              {content.title}
            </h1>
            <p className="text-slate-400 text-sm">
              {content.lastUpdate}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-10"
          >
            {content.sections.map((section, index) => (
              <div key={index} className="border-b border-slate-100 pb-8 last:border-0">
                <h2 className="font-['Playfair_Display'] text-xl md:text-2xl font-semibold text-[#283880] mb-4">
                  {section.title}
                </h2>
                <div className="text-[#64748B] leading-relaxed whitespace-pre-line text-sm md:text-base">
                  {section.content}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
